import type { RiftPreviewCallback } from './riftPresentation';
import type { EncounterCardInfo, GameState, Investigator, Location, MythosCard, SpecialCard, UserAction } from '../api';
import { getGateDestinations } from './GateMarker';
import { OUTSIDE_HELP_TEXT } from './DigitalLocationRules';
import { buildDigitalMapLayout, nodeLayout, DigitalMapLayout, Point, visibleLocations, ExpansionFlags, worldName } from './digitalMapGeometry';

export type DigitalMapGameplay = {
  gameState?: GameState;
  locationActions?: UserAction[];
  loading?: boolean;
  onExecuteAction?: (id: string) => void;
  investigatorsByName?: Record<string, Investigator>;
  currentInvestigator?: Investigator;
  highlightedInvestigators?: string[];
  viewerName?: string;
  onInspectRifts?: () => void;
  onPreviewRift?: RiftPreviewCallback;
  movementRing?: MovementActionRing;
};
export type MovementActionRing = { location: Location; actions: UserAction[] };
export const RARE_BOOK_COLLECTION_RULE = 'Rare Book Collection: Instead of having an encounter here, you may lose 1 Sanity to gain 1 Clue token.';

/** Presentation only: global faceup cards add rules without mutating game-state locations. */
export function resolveMapLocations(locations: Location[], cards: SpecialCard[] = []): Location[] {
  const rareBook = cards.some(card => card.name.trim().toLowerCase() === 'rare book collection');
  let changed = false;
  const normalized = (text: string) => text.trim().replace(/\s+/g, ' ').toLowerCase();
  const resolved = locations.map(location => {
    if (location.name === 'Sawbone Alley' && !location.specialEncounterText?.trim()) {
      changed = true;
      return { ...location, specialEncounterText: OUTSIDE_HELP_TEXT };
    }
    if (!rareBook || location.name !== 'Library' || location.type !== 'Location') return location;
    const original = location.specialEncounterText || '';
    if (normalized(original).includes(normalized(RARE_BOOK_COLLECTION_RULE))) return location;
    changed = true;
    return { ...location, specialEncounterText: [original.trim(), RARE_BOOK_COLLECTION_RULE].filter(Boolean).join('\n') };
  });
  return changed ? resolved : locations;
}

export const matchingLocationActions = (name: string, actions: UserAction[] = []) => actions.filter(action =>
  (action.isLocationAction || action.locationName) && (action.locationName === name ||
    (action.card && typeof action.card === 'object' && !Array.isArray(action.card) && 'name' in action.card && action.card.name === name)));

// Presentation only: availability and execution always come from the actual UserAction.
const movementPrefixes = ['move_to_', 'patrol_drive_', 'falcon_point_sail_', 'falcon_point_pull_',
  'other_world_return_', 'white_ship_move_', 'return_to_', 'choose_destination_'];
export const isMovementAction = (action: UserAction) => action.id === 'other_world_move_area_2' || movementPrefixes.some(prefix => action.id.startsWith(prefix));

export const isMovementCompletion = (action: UserAction) =>
  ['end_movement', 'confirm_end_movement', 'finish_movement_end_actions'].includes(action.id);

/** Main menus have stable IDs; nested prompts retain the phase but do not have these anchors. */
export function movementActionRing(locations: Location[], context: DigitalMapGameplay): MovementActionRing | undefined {
  if (context.gameState?.currentPhase !== 'Movement') return undefined;
  const actions = context.locationActions ?? [];
  const anchor = actions.find(isMovementCompletion) ?? actions.find(action =>
    action.id === 'other_world_move_area_2' || action.id.startsWith('other_world_return_'));
  if (!anchor) return undefined;
  const actors = Array.from(new Set(actions.filter(action => action.cardType !== 'Investigator')
    .map(action => action.investigatorName?.trim().toLowerCase()).filter(Boolean)));
  const actor = anchor.investigatorName?.trim().toLowerCase() ?? (actors.length === 1 ? actors[0] : undefined);
  if (!actor) return undefined;
  const location = locations.find(place => place.investigatorNames?.some(name => name.trim().toLowerCase() === actor));
  if (!location || (!isMovementCompletion(anchor) && location.type !== 'OtherWorld')) return undefined;
  return { location, actions: actions.filter(action => !(isMovementAction(action) || action.id.startsWith('silas_able_seaman_'))
    || !locations.some(place => matchingLocationActions(place.name, [action]).length > 0)) };
}

/** Popup entries follow the original menu order, even when location and ring membership overlap. */
export function inspectionLocationActions(places: Location[], context: DigitalMapGameplay): UserAction[] {
  const ring = context.movementRing;
  const ringIds = new Set(ring && places.some(place => place.name === ring.location.name) ? ring.actions.map(action => action.id) : []);
  const seen = new Set<string>();
  return (context.locationActions ?? []).filter(action => {
    if (seen.has(action.id) || (!ringIds.has(action.id) && !places.some(place => matchingLocationActions(place.name, [action]).length))) return false;
    seen.add(action.id);
    return true;
  });
}
export const investigatorFor = (name: string, context: DigitalMapGameplay) =>
  context.investigatorsByName?.[name] ?? context.gameState?.investigatorsByName?.[name];
export const highlightedInvestigator = (name: string, context: DigitalMapGameplay) => context.currentInvestigator
  ? context.currentInvestigator.name === name : !!context.highlightedInvestigators?.includes(name);
export function revealedEncounter(location: Location, state?: GameState): EncounterCardInfo | undefined {
  return state?.decks?.encounters?.[location.name]?.topCard ?? Object.values(state?.decks?.encounters ?? {})
    .map(deck => deck.topCard).find(card => card?.locationName === location.name) ?? undefined;
}
export function destinationInvestigators(location: Location, locations: Location[]) {
  const destinations = new Set((location.gates ?? []).flatMap(getGateDestinations).map(name => name.replace(/\s/g, '').toLowerCase()));
  return Array.from(new Set(locations.filter(l => l.type === 'OtherWorld' && destinations.has(worldName(l.name).replace(/\s/g, '').toLowerCase()))
    .flatMap(l => l.investigatorNames ?? [])));
}
export function locationActivities(location: Location, state?: GameState): MythosCard[] {
  const seen = new Set<string>();
  return [state?.activeRumor, state?.activeMythos, state?.activeProcess].filter((card): card is MythosCard => {
    if (!card || (card.rumorLocation || card.activityLocationName) !== location.name) return false;
    const key = card.id || JSON.stringify([card.name, card.type]);
    if (seen.has(key)) return false;
    seen.add(key); return true;
  });
}
export const canShowMonsters = (location: Location) => location.type !== 'OtherWorld' && location.name !== 'Lost in Time and Space';
export const canShowRifts = canShowMonsters;
export function locationRiftProgress(location: Location, state?: GameState) {
  return (state?.riftProgress ?? []).flatMap(entry => (entry.tracks ?? []).flatMap(track => (track.locations ?? [])
    .filter(name => name === location.name).map(name => ({ name, isOpen: entry.isOpen, id: entry.id }))));
}


/** Resolve a controller's camera target independently of whose sheet happens to be open. */
export function cameraInvestigatorName(context: DigitalMapGameplay): string | undefined {
  const same = (a?: string | null, b?: string | null) => !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
  const state = context.gameState;
  const controlled = state?.players?.find(player => same(player.name, context.viewerName))?.controlledInvestigators
    ?? context.highlightedInvestigators ?? [];
  if (state?.players?.length && !controlled.length) return undefined;
  const multiplayer = (state?.expectedPlayersCount ?? 1) > 1;
  const actions = state?.availableActions ?? context.locationActions ?? [];
  const acting = actions.find(action => action.investigatorName && action.cardType !== 'Investigator'
    && (!multiplayer || controlled.some(name => same(name, action.investigatorName))))?.investigatorName;
  const selected = context.currentInvestigator?.name;
  const name = acting ?? (selected && (!multiplayer || controlled.some(value => same(value, selected))) ? selected : undefined)
    ?? (multiplayer ? controlled[0] : undefined);
  if (!name || (multiplayer && !controlled.length)) return undefined;
  return name;
}

export function investigatorCameraTarget(locations: Location[], flags: ExpansionFlags, context: DigitalMapGameplay, layout: DigitalMapLayout = buildDigitalMapLayout(flags, locations), investigatorName?: string): (Point & { name: string }) | undefined {
  const name = investigatorName ?? cameraInvestigatorName(context);
  if (!name) return undefined;
  const place = visibleLocations(locations, flags).find(location => location.investigatorNames?.some(value => value.trim().toLowerCase() === name.trim().toLowerCase()));
  if (!place) return undefined;
  const world = place.type === 'OtherWorld';
  const position = layout.positions[world ? worldName(place.name) : place.name];
  if (!position) return undefined;
  return { x: position.x + (world ? (place.name.endsWith(' 1') ? -1 : 1) * nodeLayout(worldName(place.name), '', false, false, layout).areaOffset : 0), y: position.y, name };
}
